<template>
  <Transition name="section-fade" mode="out-in">
    <section v-if="showForm && !showFormSubmission" key="intro" id="patient-form__intro-page"
      class="py-10 md:py-20 md:h-[calc(100dvh_-_var(--navbarHeight)_-_var(--wp-admin--admin-bar--height,0px))] [&.is-open]:hidden">
    <div class="justify-center row">
      <div class="w-full col md:w-[578px]">
        <div
          class="bg-white text-center border border-brand-light-gray rounded-[10px] py-8 px-5 md:py-16 md:px-[100px] flex flex-col items-center shadow-md shadow-[var(--cardShadow,transparent)]">
          <p class="mb-4 hdg">
            {{ form.content.title }}
          </p>
          <div v-if="form.content.image" class="mb-2 border rounded-full size-[96px] border-brand-light-gray flex-none">
            <img class="object-cover w-full h-full rounded-full aspect-square" :src="form.content.image" alt="">
          </div>
          <p class="mb-5 tracking-wide paragraph-small">
            {{ form.content.imageCaption }}
          </p>
          <p class="mb-3 font-medium paragraph-default">
            {{ form.content.content }}
          </p>
          <p class="paragraph-small">
            {{ form.content.directions }}
          </p>
          <button @click="showingForm" class="btn-primary">
            Begin Form
          </button>
        </div>
      </div>
    </div>
  </section>
  </Transition>
  <Transition name="section-fade" mode="out-in">
    <section
      class="w-full mx-auto brand-colors max-w-[774px] grid place-content-center h-[calc(95dvh_-_var(--topOffset))]"
      v-if="showFormSubmission" key="confirmation">
    <div
      class="grid bg-white py-8 px-5 md:p-[100px] place-content-center border border-brand-light-gray rounded-[10px]">
      <h4 class="mb-3 text-center hdg">Thank You for Your Submission!</h4>
      <p class="text-center paragraph-default">
        We have successfully received your intake form submission. Our team will review the information, and help
        provide you solutions to any of your concerns.
      </p>
    </div>
  </section>
  </Transition>
  <form v-if="!showFormSubmission" class="md:px-[55px] max-w-[1280px] w-full mx-auto brand-colors pb-[100px]"
    @submit.prevent="submitForm">
    <Transition name="section-fade" mode="out-in" @after-enter="onSectionEnter">
      <div v-if="!showForm && showConcerns" key="concerns">
        <div v-for="(section, index) in reactiveSections" :key="index">
          <div class="create-form_form-section">
            <div>
              <div :style="{ borderColor: formColors.divider_line_color }"
                class="flex items-end justify-between w-full gap-5 pb-3 mt-6 mb-6 border-b">
                <div>
                  <p :style="{ color: formColors.heading_text_color }" class="mb-1 font-semibold paragraph-large">
                    {{ section.name }}
                  </p>
                  <p :style="{ color: formColors.body_text_color }" class="paragraph-small">
                    {{ section.direction }}
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-3 md:gap-5 lg:grid-cols-4 md:grid-cols-2 concern-grid">
                <template v-if="sections && sections.length > 0">
                  <div v-for="(concern, concernIndex) in section.concerns" :key="concernIndex" class="flex flex-col h-full concern-item">
                    <template v-if="!concern.includes('text_field')">
                      <input :type="'checkbox'" :id="concern + concernIndex + index" :name="concern + concernIndex + index"
                        :value="{ sectionIndex: index, sectionName: section.name, concern }" v-model="selectedConcerns"
                        class="font-semibold paragraph-small text-brand-eggplant" />
                      <label
                        :style="{ backgroundColor: formColors.checkmark_inactive_button_color, color: formColors.checkmark_inactive_text_color }"
                        :for="concern + concernIndex + index" class="grid h-full place-content-center">
                        {{ filterText(concern) }}
                      </label>
                    </template>
                    <div v-else class="flex flex-col flex-1">
                      <input :type="'text'" :id="concern + concernIndex + index" :name="concern + concernIndex + index"
                        v-model="textConcerns[index + '|' + concern + concernIndex]"
                        class="font-semibold paragraph-small text-brand-eggplant" />
                      <label class="flex items-center flex-1 -mt-3"
                        :style="{ color: formColors.checkmark_inactive_text_color }" :for="concern + concernIndex + index">
                        {{ filterText(concern) }}
                      </label>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="showFormContact" key="contact" class="contact-section">
        <div class="w-90% max-w-[574px] mx-auto pt-16">
          <div class="mb-3 text-center contact-heading hdg">
            Almost Done!
          </div>
          <p class="text-center contact-subtext paragraph-default">
            Enter your contact information to instantly send your form to your provider.
          </p>

          <div class="grid mt-4 form brand-colors">
            <div class="form-group contact-field">
              <label for="firstName" class="block mb-2 text-sm font-medium">First Name</label>
              <input type="text" id="firstName" v-model="contactInfo.firstName" class="w-full px-3 py-2 border rounded-md"
                required />
            </div>
            <div class="form-group contact-field">
              <label for="lastName" class="block mb-2 text-sm font-medium">Last Name</label>
              <input type="text" id="lastName" v-model="contactInfo.lastName" class="w-full px-3 py-2 border rounded-md"
                required />
            </div>
            <div class="form-group contact-field">
              <label for="phone" class="block mb-2 text-sm font-medium">Phone Number</label>
              <input type="tel" id="phone" v-model="contactInfo.phone" class="w-full px-3 py-2 border rounded-md"
                required />
            </div>
            <div class="form-group contact-field">
              <label for="email" class="block mb-2 text-sm font-medium">Email Address</label>
              <input type="email" id="email" v-model="contactInfo.email" class="w-full px-3 py-2 border rounded-md"
                required />
            </div>
            <button v-if="showFormContact" type="submit"
              class="flex items-center justify-center px-5 py-2 font-semibold text-center rounded-full contact-field btn-primary">
              Submit Request
            </button>
          </div>
        </div>
      </div>
    </Transition>


    <div class="fixed bottom-0 left-0 w-full h-[52px] md:h-[72px] shadow-footer" v-if="!showForm">
      <div class="container h-full">
        <div class="flex items-center h-full my-auto">
          <div class="flex gap-4 ml-auto" v-if="showConcerns">
            <button type="button" @click="showNext" class="mt-0 btn-primary">
              Next
            </button>
          </div>
          <div class="flex gap-4" v-else>
            <button @click="back"
              class="flex items-center font-semibold transition duration-300 hover:text-brand-indigo" type="button">
              <svg class='mr-1 icon icon-chevron-left'>
                <use xlink:href='#icon-chevron-left'></use>
              </svg>
              Back
            </button>
          </div>
        </div>
      </div>
    </div>

    <loading :showLoading="isLoading"></loading>
  </form>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import axios from 'axios';
import { gsap } from 'gsap';

const props = defineProps({
  postId: Number,
  userId: Number,
  postHash: String,
  sections: Array,
  form: Object,
  formColors: Object,
  emailColors: Object
});

const reactiveSections = reactive([...props.sections]);
const isLoading = ref(false);
const isSaving = ref(false);
const saveStatus = ref('');
const selectedConcerns = ref([]);
const textConcerns = ref({});
const showFormSubmission = ref(false);

const filterText = (concern) => {
  return concern.replace(/:\[text_field\]:/g, '');
};

const selectedBySection = computed(() => {
  const concernsBySection = {};
  selectedConcerns.value.forEach(({ sectionIndex, sectionName, concern }) => {
    if (!concernsBySection[sectionIndex]) {
      concernsBySection[sectionIndex] = [];
    }
    concernsBySection[sectionIndex].push(concern);
  });
  return concernsBySection;
});

const back = () => {
  showConcerns.value = !showConcerns.value;
  showFormContact.value = !showFormContact.value;
}

const contactInfo = reactive({
  firstName: '',
  lastName: '',
  phone: '',
  email: ''
});

const showForm = ref(true);
const showingForm = () => {
  showForm.value = !showForm.value;
}

const showConcerns = ref(true);
const showFormContact = ref(false);
const showNext = () => {
  showConcerns.value = !showConcerns.value;
  showFormContact.value = !showFormContact.value;
}

// Runs after the Transition finishes entering the new section
const onSectionEnter = (el) => {
  // Concern grid stagger
  const items = el.querySelectorAll('.concern-item');
  if (items.length) {
    gsap.fromTo(items,
      { opacity: 0, y: 16, scale: 0.96 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.35,
        stagger: 0.04,
        ease: 'power2.out',
        clearProps: 'transform'
      }
    );
  }

  // Contact form stagger
  if (el.classList.contains('contact-section')) {
    const heading = el.querySelector('.contact-heading');
    const subtext = el.querySelector('.contact-subtext');
    const fields = el.querySelectorAll('.contact-field');

    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

    if (heading) {
      tl.fromTo(heading,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.4 }
      );
    }
    if (subtext) {
      tl.fromTo(subtext,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.35 },
        '-=0.2'
      );
    }
    if (fields.length) {
      tl.fromTo(fields,
        { opacity: 0, y: 18 },
        {
          opacity: 1,
          y: 0,
          duration: 0.35,
          stagger: 0.08,
          clearProps: 'transform'
        },
        '-=0.15'
      );
    }
  }
};

// create color variables for all colors set in style
const root = document.documentElement;
for (const [key, value] of Object.entries(props.formColors)) {
  const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
  root.style.setProperty(`--${camelKey}`, value);
}

const submitForm = async () => {
  isSaving.value = true;
  saveStatus.value = '';
  isLoading.value = true;

  try {
    const response = await axios({
      method: 'POST',
      url: `${siteInfo.wpApiSettings.ll}form/save`,
      headers: {
        'X-WP-Nonce': siteInfo.wpApiSettings.nonce,
        'X-Requested-With': 'XMLHttpRequest'
      },
      data: {
        userId: props.userId,
        postId: props.postId,
        selectedConcerns: selectedConcerns.value,
        textConcerns: textConcerns.value,
        contactInfo: {
          firstName: contactInfo.firstName,
          lastName: contactInfo.lastName,
          phone: contactInfo.phone,
          email: contactInfo.email
        }
      }
    });

    showFormSubmission.value = !showFormSubmission.value;
    if (response.status === 200) {
      saveStatus.value = 'Form saved successfully!';
      console.log('Form status updated successfully');
    }
  } catch (error) {
    console.error('Error updating form status:', error);
    saveStatus.value = 'Error saving form. Please try again.';
  } finally {
    isLoading.value = false;
    isSaving.value = false; // Reset saving state
  }
};
</script>

<style scoped>
/* Section fade transition (intro → concerns → contact → confirmation) */
.section-fade-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.section-fade-leave-active {
  transition: opacity 0.2s ease;
}
.section-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.section-fade-leave-to {
  opacity: 0;
}

/* Ensure concern items start hidden for GSAP stagger */
.concern-item {
  opacity: 0;
}

/* Contact form fields start hidden for GSAP stagger */
.contact-heading,
.contact-subtext,
.contact-field {
  opacity: 0;
}
</style>
